import * as React from "react";
import { memo } from "react";
import styled from "styled-components";
import * as h from "../../../ui/_themes/helpers";

export interface IBetSliderProps {
  className?: string;
  values: number[];
  value: number;
  onValueUpdate(value: number): void;
  formatter?(value: number): string;
}

const defaultFormatter = (value: number) => value + "";

const View = ({
  className,
  values,
  value,
  onValueUpdate,
  formatter = defaultFormatter
}: IBetSliderProps) => {
  const index = values.indexOf(value);
  const length = values.length;
  const min = values[0];
  const max = values[values.length - 1];
  const prev = values[Math.max(index - 1, 0)];
  const next = values[Math.min(index + 1, length - 1)];

  const onPrevValueSelected = () => {
    onValueUpdate(prev);
  };

  const onNextValueSelected = () => {
    onValueUpdate(next);
  };

  return (
    <main className={className}>
      <section>
        <i data-disabled={value === prev} onClick={onPrevValueSelected}>
          <svg width="24" height="24" viewBox="0 0 24 24">
            <g>
              <path d="M0 0h24v24H0z" fill="none" />
              <path d="M7 11v2h10v-2H7zm5-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" />
            </g>
          </svg>
        </i>
        <div>
          <div>
            <div />
            <div />
            <div>
              <ul>
                {Array(length)
                  .fill(null)
                  .map((_, key) => (
                    <li key={key} />
                  ))}
              </ul>
            </div>
          </div>
        </div>
        <i data-disabled={value === next} onClick={onNextValueSelected}>
          <svg width="24" height="24" viewBox="0 0 24 24">
            <g>
              <path d="M0 0h24v24H0z" fill="none" />
              <path d="M13 7h-2v4H7v2h4v4h2v-4h4v-2h-4V7zm-1-5C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" />
            </g>
          </svg>
        </i>
      </section>
      <section>
        <span>{formatter(min)}</span>
        <span>{formatter(max)}</span>
      </section>
    </main>
  );
};

export const BetSlider = memo(styled(View)`
  box-sizing: border-box;
  color: white;
  display: flex;
  flex-direction: column;

  > section:nth-child(1) {
    display: flex;
    flex: 1 0 auto;
    height: 3em;

    /* plus & minus btns */
    > i {
      flex: 0 0 auto;
      display: flex;
      justify-content: center;
      align-items: center;
      transition: color 100ms ease-in-out;
      -webkit-tap-highlight-color: transparent;
      color: white;

      @media (hover: hover) {
        &:hover {
          cursor: pointer;
          color: #c7c7c7;
        }
      }

      &:active {
        cursor: pointer;
        color: #a7a7a7;
      }

      &[data-disabled="true"] {
        color: #888888;

        touch-action: none;
        pointer-events: none;
        user-select: none;
      }

      > svg {
        width: 3em;
        height: 100%;
        font-size: 0.9em;
        fill: currentColor;
      }
    }

    /*  slider */
    > div {
      flex: 1;
      display: flex;
      align-items: center;

      touch-action: none;
      pointer-events: none;
      user-select: none;

      > * {
        touch-action: none;
        pointer-events: none;
        user-select: none;
      }

      > div {
        margin: 0 1em;
        flex: 1;
        height: 0.2em;
        position: relative;

        /* slider track */
        > div:nth-child(1) {
          position: absolute;
          height: 100%;
          width: 100%;
          background-color: grey;
        }

        /* slider progress */
        > div:nth-child(2) {
          position: absolute;
          height: 100%;
          width: ${props =>
            100 *
            (props.values.indexOf(props.value) / (props.values.length - 1))}%;
          background-color: ${h.getBackgroundColor(
            "betSliderTrack",
            "betSliderTrack"
          )};
        }

        /* slider steps */
        > div:nth-child(3) {
          position: absolute;
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;

          > ul {
            height: 100%;
            flex: 1;
            list-style: none;
            margin: 0;
            padding: 0;
            color: red;

            display: flex;
            justify-content: space-between;

            > li {
              flex: 0 0 3px;
              background-color: white;
            }
          }
        }
      }
    }
  }

  > section:nth-child(2) {
    margin: 0 4em;
    display: flex;

    > span {
      font-weight: bold;
      color: rgba(255, 255, 255, 0.4);
      flex: 1;
      display: flex;
      margin-top: -0.5em;

      touch-action: none;
      pointer-events: none;
      user-select: none;

      &:nth-child(1) {
        margin-left: -${props => props.values[0].toString().length * 0.3 + 0.2}em;
      }

      &:nth-child(2) {
        margin-right: -${props => props.values[props.values.length - 1].toString().length * 0.3 + 0.1}em;
        justify-content: flex-end;
      }
    }
  }
`);
